import React, { useCallback, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  Chip,
  Skeleton,
  Alert,
  IconButton,
  Tooltip,
} from "@mui/material";
import {
  Refresh as RefreshIcon,
  SportsBaseball as BaseballIcon,
  Schedule as ScheduleIcon,
} from "@mui/icons-material";
import { useGetTodaysScoresQuery } from "../../store/api/mlbApi";
import TeamLogo from "../TeamLogo";
import HomeSection from "./HomeSection";
import { homeCardHover, horizontalScroll } from "./homeStyles";

const formatGameTime = (gameDate) => {
  if (!gameDate) return "TBD";
  try {
    return new Date(gameDate).toLocaleTimeString("en-US", {
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    });
  } catch {
    return "TBD";
  }
};

const getGameStatus = (status) => {
  if (!status) return { color: "default", text: "Unknown" };
  const { abstractGameState, detailedState } = status;
  switch (abstractGameState) {
    case "Final":
      return { color: "success", text: "Final" };
    case "Live":
      return { color: "warning", text: detailedState || "Live" };
    case "Preview":
      return { color: "info", text: "Scheduled" };
    case "Postponed":
      return { color: "error", text: "Postponed" };
    default:
      return { color: "default", text: detailedState || abstractGameState };
  }
};

const GameCard = ({ game, onClick }) => {
  const { teams, gameDate, status } = game;
  const gameStatus = getGameStatus(status);
  const isLive = status?.abstractGameState === "Live";

  return (
    <Box
      component="button"
      type="button"
      onClick={() => onClick(game.gamePk)}
      aria-label={`View details for ${teams.away.team.name} at ${teams.home.team.name}`}
      sx={{
        minWidth: { xs: 200, sm: 220 },
        flex: "0 0 auto",
        p: 1.5,
        borderRadius: 1.5,
        border: "1px solid",
        borderColor: isLive ? "warning.main" : "divider",
        backgroundColor: isLive ? "rgba(245, 158, 11, 0.06)" : "transparent",
        cursor: "pointer",
        textAlign: "left",
        color: "inherit",
        font: "inherit",
        ...homeCardHover,
      }}
    >
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
        <Typography variant="caption" color="text.secondary" sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          <ScheduleIcon sx={{ fontSize: 14 }} />
          {formatGameTime(gameDate)}
        </Typography>
        <Chip label={gameStatus.text} color={gameStatus.color} size="small" variant="outlined" sx={{ height: 22 }} />
      </Box>

      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 1 }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mb: 0.25 }}>
            <TeamLogo
              mlbTeamId={teams.away.team.id}
              teamName={teams.away.team.name}
              size={20}
              alt={`${teams.away.team.name} logo`}
            />
            <Typography variant="body2" noWrap fontWeight={500}>
              {teams.away.team.name}
            </Typography>
          </Box>
          <Typography variant="h6" fontWeight={700} color="text.primary">
            {teams.away.score ?? "–"}
          </Typography>
        </Box>
        <Typography variant="caption" color="text.secondary">
          @
        </Typography>
        <Box sx={{ flex: 1, minWidth: 0, textAlign: "right" }}>
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 0.75, mb: 0.25 }}>
            <Typography variant="body2" noWrap fontWeight={500}>
              {teams.home.team.name}
            </Typography>
            <TeamLogo
              mlbTeamId={teams.home.team.id}
              teamName={teams.home.team.name}
              size={20}
              alt={`${teams.home.team.name} logo`}
            />
          </Box>
          <Typography variant="h6" fontWeight={700} color="text.primary">
            {teams.home.score ?? "–"}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

const TodaySlate = () => {
  const navigate = useNavigate();
  const [refreshing, setRefreshing] = useState(false);
  const { data: scoresData, isLoading, error, refetch } = useGetTodaysScoresQuery();

  const handleGameClick = useCallback(
    (gamePk) => {
      navigate(`/app/game/${gamePk}`);
    },
    [navigate]
  );

  const handleRefresh = useCallback(async () => {
    setRefreshing(true);
    try {
      await refetch();
    } finally {
      setRefreshing(false);
    }
  }, [refetch]);

  const games = scoresData?.success ? scoresData.data ?? [] : [];

  const headerAction = (
    <Tooltip title="Refresh scores">
      <IconButton size="small" onClick={handleRefresh} disabled={refreshing || isLoading}>
        <RefreshIcon fontSize="small" />
      </IconButton>
    </Tooltip>
  );

  return (
    <HomeSection
      title="Today's slate"
      subtitle={isLoading ? undefined : "Current season scores — " + `${games.length} game${games.length === 1 ? "" : "s"} today`}
      action={headerAction}
      noPadding
    >
      <Box sx={{ p: 2 }}>
        {isLoading && (
          <Box sx={horizontalScroll}>
            {[1, 2, 3].map((i) => (
              <Skeleton key={i} variant="rounded" width={220} height={88} sx={{ flexShrink: 0 }} />
            ))}
          </Box>
        )}

        {error && (
          <Alert
            severity="error"
            action={
              <IconButton color="inherit" size="small" onClick={handleRefresh}>
                <RefreshIcon />
              </IconButton>
            }
          >
            Failed to load scores
          </Alert>
        )}

        {!isLoading && !error && games.length === 0 && (
          <Box sx={{ textAlign: "center", py: 4 }}>
            <BaseballIcon sx={{ fontSize: 40, color: "text.secondary", mb: 1, opacity: 0.5 }} />
            <Typography variant="body2" color="text.secondary">
              No games scheduled for today
            </Typography>
          </Box>
        )}

        {!isLoading && !error && games.length > 0 && (
          <Box sx={horizontalScroll}>
            {games.map((game) => (
              <GameCard key={game.gamePk} game={game} onClick={handleGameClick} />
            ))}
          </Box>
        )}
      </Box>
    </HomeSection>
  );
};

export default TodaySlate;
